// components/ReportFilters.jsx
import React from "react";
import {
  Grid,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Box,
  Tooltip,
  MenuItem,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import BlockIcon from "@mui/icons-material/Block";

import Datetime from "react-datetime";
import { useTheme } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles({
  formControl: {
    "& .MuiInputBase-root": {
      color: "#666",
      height: "40px",
      minWidth: "120px",
      fontSize: "14px",
    },
    "& .MuiInputLabel-root": {
      fontSize: "14px",
    },
  },
});

export const ReportFilters = ({
  filters,
  onFilterChange,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onSearch,
  onReset,
  onExport,
  onBlock,
  selectedCount,
  isCallBlockAllowed,
  searchDestination,
  onSearchDestinationChange,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (

    <Box
      className="cdr_filter_row"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        pb: 1,
      }}
    >
    <Box
      sx={{
        // Single centred row — filter widgets + action buttons are one
        // page-centred group. Wraps onto multiple lines on narrow screens
        // while keeping each line centred.
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.75,
      }}
    >
        {/* Caller ID */}
        <Box sx={{ flex: "0 0 auto", width: 150 }}>
          <TextField
            className={`${classes.formControl} textfield_select`}
            fullWidth
            size="small"
            type="text"
            label="Caller ID"
            variant="outlined"
            value={filters.callerId}
            onChange={(e) => onFilterChange("callerId", e.target.value)}
          />
        </Box>

        {/* DID Number */}
        <Box sx={{ flex: "0 0 auto", width: 150 }}>
          <TextField
            className={`${classes.formControl} textfield_select`}
            fullWidth
            size="small"
            type="text"
            label="DID Number"
            variant="outlined"
            value={filters.didNumber}
            onChange={(e) => onFilterChange("didNumber", e.target.value)}
          />
        </Box>

        {/* Forward Number */}
        <Box sx={{ flex: "0 0 auto", width: 150 }}>
          <TextField
            className={`${classes.formControl} textfield_select`}
            fullWidth
            size="small"
            type="text"
            label="Forward Number"
            variant="outlined"
            value={filters.destination}
            onChange={(e) => onFilterChange("destination", e.target.value)}
          />
        </Box>

        {/* Unique rows (dedupe by caller_id_number) */}
        <Box sx={{ display: "flex", alignItems: "center", flex: "0 0 auto", px: 0.5 }}>
          <Tooltip title="Show only unique rows by Caller ID" arrow>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!filters.uniqueOnly}
                  onChange={(e) => onFilterChange("uniqueOnly", e.target.checked)}
                />
              }
              label="Unique"
              sx={{
                m: 0,
                "& .MuiFormControlLabel-label": { fontSize: 13, fontWeight: 500 },
              }}
            />
          </Tooltip>
        </Box>

        {/* Duration (operator + value) */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: "0 0 auto" }}>
          <TextField
            className="textfield_select"
            size="small"
            select
            label="Op"
            variant="outlined"
            value={filters.durationOp || ">="}
            onChange={(e) => onFilterChange("durationOp", e.target.value)}
            sx={{
              width: 72,
              "& .MuiInputBase-root": { minWidth: 0, height: 40, fontSize: 14 },
              "& .MuiInputLabel-root": { fontSize: 14 },
            }}
          >
            <MenuItem value="=">=</MenuItem>
            <MenuItem value="!=">≠</MenuItem>
            <MenuItem value="<">&lt;</MenuItem>
            <MenuItem value="<=">≤</MenuItem>
            <MenuItem value=">">&gt;</MenuItem>
            <MenuItem value=">=">≥</MenuItem>
          </TextField>
          <TextField
            className="textfield_select"
            size="small"
            type="number"
            label="Sec"
            variant="outlined"
            value={filters.durationValue || ""}
            onChange={(e) => onFilterChange("durationValue", e.target.value)}
            inputProps={{ min: 0, maxLength: 3 }}
            sx={{
              width: 88,
              "& .MuiInputBase-root": { minWidth: 0, height: 40, fontSize: 14 },
              "& .MuiInputLabel-root": { fontSize: 14 },
            }}
          />
        </Box>

      {/* Action Buttons — inline siblings of the filters so the parent's
          justifyContent: center treats them as part of the same centred
          group. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flexShrink: 0,
        }}
      >
          <Button
            variant="contained"
            onClick={onSearch}
            startIcon={<SearchIcon sx={{ fontSize: 26 }} />}
            size="small"
            sx={{
              background: "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
              color: "#fff !important",
              textTransform: "none",
              fontSize: "13px !important",
              fontWeight: 600,
              height: 40,
              padding: "4px 14px !important",
              minWidth: 0,
              whiteSpace: "nowrap",
              borderRadius: "8px !important",
              boxShadow: "0 4px 12px rgba(22, 163, 74, 0.35)",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #15803d 0%, #166534 100%)",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 16px rgba(22, 163, 74, 0.45)",
              },
            }}
          >
            Apply Filters
          </Button>

          <Button
            variant="contained"
            onClick={onReset}
            startIcon={<RestartAltIcon sx={{ fontSize: 26 }} />}
            size="small"
            sx={{
              background: "linear-gradient(135deg, #64748b 0%, #475569 100%)",
              color: "#fff !important",
              textTransform: "none",
              fontSize: "13px !important",
              fontWeight: 600,
              height: 40,
              padding: "4px 14px !important",
              minWidth: 0,
              whiteSpace: "nowrap",
              borderRadius: "8px !important",
              boxShadow: "0 4px 12px rgba(71, 85, 105, 0.35)",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #475569 0%, #334155 100%)",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 16px rgba(71, 85, 105, 0.45)",
              },
            }}
          >
            Reset
          </Button>

          {selectedCount > 0 && (
            <Button
              variant="contained"
              disabled={selectedCount === 0}
              onClick={onBlock}
              startIcon={<BlockIcon sx={{ fontSize: 26 }} />}
              size="small"
              sx={{
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                color: "#fff !important",
                textTransform: "none",
                fontSize: "13px !important",
                fontWeight: 600,
                height: 40,
                padding: "4px 14px !important",
                minWidth: 0,
                whiteSpace: "nowrap",
                borderRadius: "8px !important",
                boxShadow: "0 4px 12px rgba(220, 38, 38, 0.35)",
                transition: "transform 0.15s ease, box-shadow 0.15s ease",
                "&:hover": {
                  background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 16px rgba(220, 38, 38, 0.45)",
                },
              }}
            >
              Block ({selectedCount})
            </Button>
          )}

          <Button
            variant="contained"
            onClick={onExport}
            startIcon={<FileDownloadIcon sx={{ fontSize: 26 }} />}
            size="small"
            sx={{
              background: "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)",
              color: "#fff !important",
              textTransform: "none",
              fontSize: "13px !important",
              fontWeight: 600,
              height: 40,
              padding: "4px 14px !important",
              minWidth: 0,
              whiteSpace: "nowrap",
              borderRadius: "8px !important",
              boxShadow: "0 4px 12px rgba(29, 78, 216, 0.35)",
              transition: "transform 0.15s ease, box-shadow 0.15s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)",
                transform: "translateY(-1px)",
                boxShadow: "0 6px 16px rgba(29, 78, 216, 0.45)",
              },
            }}
          >
            Download
          </Button>
      </Box>
    </Box>
    </Box>


  );
};
